import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Inbox } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  neu: "bg-cyan-glass/15 text-cyan-glass border-cyan-glass/30",
  "in bearbeitung": "bg-amber-400/15 text-amber-300 border-amber-400/30",
  erledigt: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30",
};

export default function Admin() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`);
      setQuotes(data);
    } catch {
      toast.error("Anfragen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}?status=${encodeURIComponent(status)}`);
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success("Status aktualisiert.");
    } catch {
      toast.error("Aktualisierung fehlgeschlagen.");
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#030712] text-white" data-testid="admin-page">
      <header className="glass-strong sticky top-0 z-10 border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="back-home" className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/10 hover:border-cyan-glass hover:text-cyan-glass transition-colors">
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <div>
              <h1 className="font-heading text-xl font-semibold tracking-tight">Anfragen-Verwaltung</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">S&amp;P Reinigungsservice</p>
            </div>
          </div>
          <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] hover:border-cyan-glass hover:text-cyan-glass transition-colors">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 md:px-10 py-12">
        <div className="mb-8 flex items-center gap-3">
          <Inbox className="h-5 w-5 text-cyan-glass" strokeWidth={1.5} />
          <span className="font-mono text-sm text-white/60">{quotes.length} Anfrage(n)</span>
        </div>

        {!loading && quotes.length === 0 ? (
          <div className="glass rounded-sm p-16 text-center text-white/50" data-testid="empty-state">
            Noch keine Anfragen eingegangen.
          </div>
        ) : (
          <div className="glass rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/40 font-mono text-xs uppercase tracking-wider">Datum</TableHead>
                  <TableHead className="text-white/40 font-mono text-xs uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-white/40 font-mono text-xs uppercase tracking-wider">Kontakt</TableHead>
                  <TableHead className="text-white/40 font-mono text-xs uppercase tracking-wider">Leistung</TableHead>
                  <TableHead className="text-white/40 font-mono text-xs uppercase tracking-wider">Nachricht</TableHead>
                  <TableHead className="text-white/40 font-mono text-xs uppercase tracking-wider">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((q) => (
                  <TableRow key={q.id} className="border-white/10 hover:bg-white/5" data-testid={`quote-row-${q.id}`}>
                    <TableCell className="text-white/50 text-sm whitespace-nowrap">{fmt(q.created_at)}</TableCell>
                    <TableCell className="font-medium text-white">{q.name}</TableCell>
                    <TableCell className="text-sm text-white/70">
                      <a href={`tel:${q.phone}`} className="block hover:text-cyan-glass">{q.phone}</a>
                      <a href={`mailto:${q.email}`} className="block hover:text-cyan-glass break-all">{q.email}</a>
                    </TableCell>
                    <TableCell className="text-white/80 text-sm">{q.service}</TableCell>
                    <TableCell className="text-white/60 text-sm max-w-xs truncate" title={q.message}>{q.message || "—"}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`status-trigger-${q.id}`}>
                            <Badge variant="outline" className={`cursor-pointer capitalize ${STATUS[q.status] || STATUS.neu}`}>
                              {q.status}
                            </Badge>
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#0A111F] border-white/15 text-white">
                          {["neu", "in bearbeitung", "erledigt"].map((s) => (
                            <DropdownMenuItem key={s} onClick={() => changeStatus(q.id, s)} className="capitalize focus:bg-cyan-glass/15 focus:text-cyan-glass">
                              {s}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
